import React, { useState } from 'react';
import { Plus, Minus } from 'lucide-react';
import Header24 from './components/Header24';
import Footer24 from './components/Footer24';

const FAQs24 = () => {
    const [activeTab, setActiveTab] = useState('orders');
    const [expandedItems, setExpandedItems] = useState({});

    const tabs = [
        { key: 'orders', label: 'Orders & Shipping' },
        { key: 'products', label: 'Products & Care' },
        { key: 'returns', label: 'Returns & Exchanges' },
        { key: 'account', label: 'Account & Payment' },
    ];

    const faqs = {
        orders: [
            {
                q: 'How long does delivery take?',
                a: 'Standard delivery within India takes 5-7 business days. Express delivery is available for select metro cities and takes 2-3 business days. International orders typically take 10-15 business days depending on the destination country and customs processing.',
            },
            {
                q: 'How can I track my order?',
                a: 'Once your order ships, you will receive a confirmation email with a tracking number and link. You can also track your order by logging into your account and visiting the Orders section. Our customer care team is available to assist with any tracking inquiries.',
            },
            {
                q: 'Do you ship internationally?',
                a: 'Yes, we ship to over 30 countries. International shipping rates are calculated at checkout based on the destination and package weight. Please note that customs duties and import taxes are the responsibility of the recipient and are not included in our shipping charges.',
            },
            {
                q: 'Can I change my delivery address after placing an order?',
                a: 'Address changes can be accommodated if the order has not yet been dispatched. Please contact us at orders@dhara.in as soon as possible. Once an order has shipped, we are unable to modify the delivery address.',
            },
        ],
        products: [
            {
                q: 'Are your products handmade?',
                a: 'Yes. Every Dhara piece is handwoven or handcrafted by skilled artisans across India. We work with weaving communities in Varanasi, Chanderi, Bhuj, and other textile centres. Due to the handmade nature, slight variations in weave, colour, and texture are natural and add to the uniqueness of each piece.',
            },
            {
                q: 'How should I care for my Dhara pieces?',
                a: 'Most of our silk and handwoven pieces should be dry cleaned only. Cotton and linen items can be gently hand washed in cold water with mild detergent. Always store your pieces flat or folded in muslin to prevent creasing and protect from direct sunlight. Detailed care instructions are included with each garment.',
            },
            {
                q: 'Will the colours look exactly as shown online?',
                a: 'We make every effort to photograph our pieces accurately. However, colours may appear slightly different depending on your screen settings and lighting conditions. The handwoven nature of our fabrics also means that each piece has its own subtle character.',
            },
            {
                q: 'Do you offer custom sizing or alterations?',
                a: 'We offer a limited made-to-measure service for select pieces. Custom orders typically take 4-6 weeks and require a virtual consultation. Please contact us at bespoke@dhara.in for more details. Standard alterations are not provided as part of the purchase.',
            },
        ],
        returns: [
            {
                q: 'What is your return policy?',
                a: 'Items may be returned within 14 days of delivery, provided they are unworn, unwashed, and in their original condition with all tags attached. Items must be returned in their original packaging. Once we receive and inspect the return, your refund will be processed within 7-10 business days.',
            },
            {
                q: 'Can I exchange an item for a different size?',
                a: 'Yes, we offer one-time size exchanges within 14 days of delivery. The item must be in its original condition with tags attached. Exchange shipping within India is complimentary. For international exchanges, return shipping is at the customer\'s expense.',
            },
            {
                q: 'What items cannot be returned?',
                a: 'Custom or made-to-order pieces, altered garments, items purchased during final sales, and saris that have been unpleated are not eligible for returns or exchanges. Gift cards are also non-refundable.',
            },
            {
                q: 'How do I initiate a return?',
                a: 'Email us at returns@dhara.in with your order number and reason for return. Our team will review your request within 24 hours and provide a prepaid return shipping label for domestic returns. Please do not ship items back without first contacting us.',
            },
        ],
        account: [
            {
                q: 'How do I create an account?',
                a: 'You can create an account by clicking "Create an account" on our sign-in page. You will need to provide your name, email address, and create a password. An account allows you to track orders, save your wishlist, and enjoy a faster checkout experience.',
            },
            {
                q: 'What payment methods do you accept?',
                a: 'We accept all major credit and debit cards (Visa, Mastercard, American Express, RuPay), UPI (Google Pay, PhonePe, Paytm), net banking, and cash on delivery for select locations within India. International orders are processed through credit/debit cards only.',
            },
            {
                q: 'Is my payment information secure?',
                a: 'Absolutely. All payment transactions are processed through secure, encrypted channels using industry-standard SSL technology. We do not store your complete card details on our servers. Our payment processing partners comply with PCI DSS security standards.',
            },
            {
                q: 'I forgot my password. How do I reset it?',
                a: 'Click "Forgot password?" on the sign-in page and enter your registered email address. You will receive a password reset link within a few minutes. If you do not receive the email, please check your spam folder or contact our support team.',
            },
        ],
    };

    const toggleExpand = (key) => {
        setExpandedItems(prev => ({ ...prev, [key]: !prev[key] }));
    };

    const currentFaqs = faqs[activeTab] || [];

    return (
        <>
            <style>{`
                @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:ital,wght@0,400;0,500;0,600;0,700;0,800;1,400;1,500&family=Inter:wght@300;400;500;600;700&display=swap');

                .dhara-faqs {
                    min-height: 100vh;
                    background: #FFFFFF;
                }
                .dhara-faqs-inner {
                    max-width: 800px;
                    margin: 0 auto;
                    padding: 80px 40px 120px;
                }
                .dhara-faqs-title {
                    font-family: 'Playfair Display', serif;
                    font-size: 42px;
                    font-weight: 400;
                    color: #1A1A1A;
                    text-align: center;
                    margin-bottom: 16px;
                    letter-spacing: -0.5px;
                }
                .dhara-faqs-subtitle {
                    font-family: 'Inter', sans-serif;
                    font-size: 16px;
                    color: #6B6B6B;
                    font-weight: 300;
                    text-align: center;
                    margin-bottom: 60px;
                }

                /* Tabs */
                .dhara-faqs-tabs {
                    display: flex;
                    gap: 0;
                    margin-bottom: 48px;
                    border-bottom: 1px solid #E5E5E5;
                    overflow-x: auto;
                }
                .dhara-faqs-tab {
                    font-family: 'Inter', sans-serif;
                    font-size: 13px;
                    font-weight: 400;
                    letter-spacing: 0.5px;
                    color: #6B6B6B;
                    padding: 14px 20px;
                    border: none;
                    background: none;
                    cursor: pointer;
                    position: relative;
                    transition: color 0.3s ease;
                    white-space: nowrap;
                }
                .dhara-faqs-tab.active {
                    color: #1A1A1A;
                    font-weight: 500;
                }
                .dhara-faqs-tab.active::after {
                    content: '';
                    position: absolute;
                    bottom: -1px;
                    left: 0;
                    right: 0;
                    height: 2px;
                    background: #00553A;
                }
                .dhara-faqs-tab:hover {
                    color: #1A1A1A;
                }

                /* Accordion */
                .dhara-faq-item {
                    border-bottom: 1px solid #E5E5E5;
                }
                .dhara-faq-question {
                    display: flex;
                    align-items: center;
                    justify-content: space-between;
                    width: 100%;
                    padding: 24px 0;
                    background: none;
                    border: none;
                    cursor: pointer;
                    text-align: left;
                    gap: 24px;
                }
                .dhara-faq-question-text {
                    font-family: 'Inter', sans-serif;
                    font-size: 15px;
                    font-weight: 400;
                    color: #1A1A1A;
                    line-height: 1.4;
                }
                .dhara-faq-icon {
                    flex-shrink: 0;
                    color: #00553A;
                    transition: transform 0.3s ease;
                }
                .dhara-faq-answer {
                    padding: 0 0 28px;
                    font-family: 'Inter', sans-serif;
                    font-size: 14px;
                    color: #6B6B6B;
                    line-height: 1.9;
                    font-weight: 300;
                    max-width: 640px;
                }

                /* Contact CTA */
                .dhara-faqs-cta {
                    text-align: center;
                    margin-top: 80px;
                    padding: 48px;
                    background: #FAFAF8;
                }
                .dhara-faqs-cta-title {
                    font-family: 'Playfair Display', serif;
                    font-size: 24px;
                    font-weight: 400;
                    color: #1A1A1A;
                    margin-bottom: 12px;
                }
                .dhara-faqs-cta-text {
                    font-family: 'Inter', sans-serif;
                    font-size: 14px;
                    color: #6B6B6B;
                    margin-bottom: 28px;
                    line-height: 1.6;
                }
                .dhara-faqs-cta-email {
                    font-family: 'Inter', sans-serif;
                    font-size: 15px;
                    color: #00553A;
                    font-weight: 500;
                    text-decoration: none;
                    border-bottom: 1px solid #00553A;
                    padding-bottom: 2px;
                    transition: opacity 0.3s ease;
                    cursor: pointer;
                }
                .dhara-faqs-cta-email:hover {
                    opacity: 0.7;
                }

                @media (max-width: 768px) {
                    .dhara-faqs-inner { padding: 40px 20px 80px; }
                    .dhara-faqs-title { font-size: 32px; }
                    .dhara-faqs-tabs { gap: 0; }
                    .dhara-faqs-tab { padding: 12px 14px; font-size: 12px; }
                    .dhara-faqs-cta { padding: 32px 20px; margin-top: 48px; }
                }
            `}</style>

            <Header24 />

            <main className="dhara-faqs">
                <div className="dhara-faqs-inner">
                    <h1 className="dhara-faqs-title">Frequently Asked Questions</h1>
                    <p className="dhara-faqs-subtitle">
                        Everything you need to know about shopping with Dhara.
                    </p>

                    {/* Tabs */}
                    <div className="dhara-faqs-tabs">
                        {tabs.map(tab => (
                            <button
                                key={tab.key}
                                className={`dhara-faqs-tab ${activeTab === tab.key ? 'active' : ''}`}
                                onClick={() => {
                                    setActiveTab(tab.key);
                                    setExpandedItems({});
                                }}
                            >
                                {tab.label}
                            </button>
                        ))}
                    </div>

                    {/* Accordion */}
                    <div>
                        {currentFaqs.map((faq, i) => {
                            const key = `${activeTab}-${i}`;
                            const isExpanded = expandedItems[key];

                            return (
                                <div key={key} className="dhara-faq-item">
                                    <button
                                        className="dhara-faq-question"
                                        onClick={() => toggleExpand(key)}
                                    >
                                        <span className="dhara-faq-question-text">{faq.q}</span>
                                        <span className="dhara-faq-icon">
                                            {isExpanded ? (
                                                <Minus size={18} strokeWidth={1.5} />
                                            ) : (
                                                <Plus size={18} strokeWidth={1.5} />
                                            )}
                                        </span>
                                    </button>
                                    {isExpanded && (
                                        <div className="dhara-faq-answer">{faq.a}</div>
                                    )}
                                </div>
                            );
                        })}
                    </div>

                    {/* Contact CTA */}
                    <div className="dhara-faqs-cta">
                        <h2 className="dhara-faqs-cta-title">Still have questions?</h2>
                        <p className="dhara-faqs-cta-text">
                            Our team is here to help. Reach out and we will respond within 24 hours.
                        </p>
                        <span className="dhara-faqs-cta-email">hello@dhara.in</span>
                    </div>
                </div>
            </main>

            <Footer24 />
        </>
    );
};

export default FAQs24;
